import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layouts/DashboardLayout';
import { filesAPI } from '../../services/api';
import { 
  GitCompare, Upload, Loader2, FileText, AlertTriangle, Check, ChevronDown, ChevronUp
} from 'lucide-react';
import { Button } from '../../components/ui/button';

const FileComparePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [file1, setFile1] = useState(null);
  const [file2, setFile2] = useState(null);
  const [comparing, setComparing] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [expandedRegions, setExpandedRegions] = useState({});

  if (user?.role !== 'admin' && user?.role !== 'operator') {
    navigate('/dashboard');
    return null;
  }

  const handleCompare = async () => {
    if (!file1 || !file2) return;
    setComparing(true);
    setError('');
    setResult(null);
    try {
      const fd = new FormData();
      fd.append('file1', file1);
      fd.append('file2', file2);
      const res = await filesAPI.compareFiles(fd);
      setResult(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Vergleich fehlgeschlagen');
    } finally {
      setComparing(false);
    }
  };

  const toggleRegion = (i) => {
    setExpandedRegions(prev => ({ ...prev, [i]: !prev[i] }));
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="compare-page">
        <div>
          <h1 className="font-heading text-2xl font-bold text-white flex items-center gap-3">
            <GitCompare className="w-6 h-6 text-[#EC2229]" />
            Dateivergleich (Hex-Diff)
          </h1>
          <p className="text-slate-400 mt-1 text-sm">Vergleichen Sie zwei ECU-Dateien und sehen Sie die Unterschiede</p>
        </div>

        {/* File Inputs */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            { label: 'Original-Datei', file: file1, setter: setFile1, testId: 'compare-file1' },
            { label: 'Modifizierte Datei', file: file2, setter: setFile2, testId: 'compare-file2' },
          ].map(({ label, file, setter, testId }) => (
            <div key={testId} className="glass-card p-5">
              <p className="text-sm font-medium text-white mb-2">{label}</p>
              <label className={`flex flex-col items-center justify-center p-8 border-2 border-dashed rounded-xl cursor-pointer transition-all ${file ? 'border-emerald-500 bg-emerald-500/5' : 'border-white/20 hover:border-white/40'}`}>
                <input type="file" className="hidden" onChange={e => setter(e.target.files?.[0])} data-testid={testId} />
                {file ? (
                  <>
                    <Check className="w-8 h-8 text-emerald-500 mb-2" />
                    <p className="text-white text-sm font-medium truncate max-w-full">{file.name}</p>
                    <p className="text-xs text-slate-400">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                  </>
                ) : (
                  <>
                    <Upload className="w-8 h-8 text-slate-500 mb-2" />
                    <p className="text-sm text-slate-400">Datei auswählen</p>
                  </>
                )}
              </label>
            </div>
          ))}
        </div>

        <Button onClick={handleCompare} disabled={!file1 || !file2 || comparing} className="btn-primary w-full md:w-auto" data-testid="compare-btn">
          {comparing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Vergleiche...</> : <><GitCompare className="w-4 h-4 mr-2" /> Dateien vergleichen</>}
        </Button>

        {error && (
          <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">{error}</div>
        )}

        {/* Results */}
        {result && (
          <div className="space-y-4">
            {/* Summary */}
            <div className="glass-card p-5">
              <h3 className="text-sm font-semibold text-white mb-3">Ergebnis</h3>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <div>
                  <p className="text-[10px] text-slate-500 uppercase">Original</p>
                  <p className="text-sm text-white font-mono">{result.file1.name}</p>
                  <p className="text-xs text-slate-400">{(result.file1.size / 1024).toFixed(0)} KB</p>
                </div>
                <div>
                  <p className="text-[10px] text-slate-500 uppercase">Modifiziert</p>
                  <p className="text-sm text-white font-mono">{result.file2.name}</p>
                  <p className="text-xs text-slate-400">{(result.file2.size / 1024).toFixed(0)} KB</p>
                </div>
                <div>
                  <p className="text-[10px] text-slate-500 uppercase">Geändert</p>
                  <p className={`text-lg font-bold font-mono ${result.identical ? 'text-emerald-400' : 'text-[#EC2229]'}`}>
                    {result.total_changed_bytes} Bytes
                  </p>
                  <p className="text-xs text-slate-400">{result.change_percentage}%</p>
                </div>
                <div>
                  <p className="text-[10px] text-slate-500 uppercase">Bereiche</p>
                  <p className="text-lg font-bold font-mono text-white">{result.total_regions}</p>
                </div>
                <div>
                  <p className="text-[10px] text-slate-500 uppercase">Status</p>
                  {result.identical ? (
                    <span className="text-emerald-400 font-medium text-sm flex items-center gap-1"><Check className="w-3.5 h-3.5" /> Identisch</span>
                  ) : (
                    <span className="text-amber-400 font-medium text-sm flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Unterschiedlich</span>
                  )}
                </div>
              </div>
              {result.summary?.map((s, i) => (
                <p key={i} className="text-xs text-slate-400 mt-1">{s}</p>
              ))}
            </div>

            {/* Diff Regions */}
            {result.diff_regions?.length > 0 && (
              <div className="glass-card p-5">
                <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-[#EC2229]" />
                  Geänderte Bereiche ({result.diff_regions.length}{result.total_regions > 100 ? ' von ' + result.total_regions : ''})
                </h3>
                <div className="space-y-2 max-h-[600px] overflow-y-auto">
                  {result.diff_regions.map((region, i) => (
                    <div key={i} className="rounded-lg border border-white/[0.06] overflow-hidden" data-testid={`diff-region-${i}`}>
                      <button onClick={() => toggleRegion(i)} className="w-full flex items-center justify-between p-2 bg-white/[0.03] hover:bg-white/[0.05] transition-colors">
                        <div className="flex items-center gap-3 text-sm">
                          <span className="text-[#EC2229] font-mono text-xs">{region.offset}</span>
                          <span className="text-slate-400 text-xs">{region.length} Bytes</span>
                        </div>
                        {expandedRegions[i] ? <ChevronUp className="w-3.5 h-3.5 text-slate-500" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-500" />}
                      </button>
                      {expandedRegions[i] && (
                        <div className="p-3 bg-[#0D0D0D] text-xs font-mono space-y-2">
                          <div>
                            <span className="text-slate-600 text-[10px]">Original:</span>
                            <div className="mt-0.5 flex flex-wrap gap-x-1">
                              {region.hex_original.split(' ').map((byte, j) => (
                                <span key={j} className={region.changed_indices.includes(j) ? 'text-red-400 bg-red-500/10 px-0.5 rounded' : 'text-slate-500'}>
                                  {byte}
                                </span>
                              ))}
                            </div>
                          </div>
                          <div>
                            <span className="text-slate-600 text-[10px]">Modifiziert:</span>
                            <div className="mt-0.5 flex flex-wrap gap-x-1">
                              {region.hex_modified.split(' ').map((byte, j) => (
                                <span key={j} className={region.changed_indices.includes(j) ? 'text-emerald-400 bg-emerald-500/10 px-0.5 rounded' : 'text-slate-500'}>
                                  {byte}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default FileComparePage;
